import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './Button';
import { submitToGoogleSheets } from '../utils/googleSheets';
interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ApplicationModal({
  isOpen,
  onClose
}: ApplicationModalProps) {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [socialHandles, setSocialHandles] = useState('');
  const [whyText, setWhyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!name.trim() || !contact.trim() || !socialHandles.trim()) {
      setSubmitError('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    const payload = {
      name: name.trim(),
      contact: contact.trim(),
      social: socialHandles.trim(),
      whyFit: whyText.trim() || undefined,
      source: window.location.href,
      userAgent: navigator.userAgent
    };
    const result = await submitToGoogleSheets(payload);
    setIsSubmitting(false);
    if (result.success) {
      setIsSubmitted(true);
    } else {
      setSubmitError(result.error || 'Something went wrong. Please try again.');
    }
  };
  const isValid = name.trim() && contact.trim() && socialHandles.trim();
  const resetForm = () => {
    setName('');
    setContact('');
    setSocialHandles('');
    setWhyText('');
    setIsSubmitted(false);
    setSubmitError(null);
  };
  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };
  return <AnimatePresence data-id="element-3">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-navy-950/80 backdrop-blur-sm z-50" data-id="element-4" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && handleClose()} onKeyDown={e => e.key === 'Escape' && handleClose()} data-id="element-5">
            <div className="relative w-full max-w-lg bg-navy-900 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-6">
              {/* Gradient top border */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-mint-500 to-wave-500" data-id="element-7" />

              {/* Close button */}
              <button onClick={handleClose} className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10" aria-label="Close modal" data-id="element-8">
                <X className="w-5 h-5" data-id="element-9" />
              </button>

              <div className="p-8" data-id="element-10">
                {isSubmitted ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-8" data-id="element-11">
                    <div className="w-16 h-16 rounded-full bg-mint-500/10 flex items-center justify-center mx-auto mb-6" data-id="element-12">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.2,
                  type: 'spring'
                }} data-id="element-13">
                        <svg className="w-8 h-8 text-mint-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-14">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-15" />
                        </svg>
                      </motion.div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3" data-id="element-16">
                      Application Received
                    </h3>
                    <p className="text-slate-400 mb-6" data-id="element-17">
                      Thanks for applying to the Creator Lab. We'll review your
                      application and get back to you soon.
                    </p>
                    <Button variant="secondary" onClick={handleClose} data-id="element-18">
                      Close
                    </Button>
                  </motion.div> : <>
                    <h2 id="modal-title" className="text-2xl font-bold text-white mb-2" data-id="element-19">
                      Apply to the Creator Lab
                    </h2>
                    <p className="text-slate-400 mb-8" data-id="element-20">
                      4 weeks • 5 creators • $2,000 stipend • Fully remote
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-id="element-21">
                      {/* Name */}
                      <div data-id="element-22">
                        <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-23">
                          Name <span className="text-mint-500" data-id="element-24">*</span>
                        </label>
                        <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} required className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-mint-500/50 focus:border-mint-500 transition-colors" placeholder="Your name" data-id="element-25" />
                      </div>

                      {/* Contact */}
                      <div data-id="element-26">
                        <label htmlFor="contact" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-27">
                          Email or phone{' '}
                          <span className="text-mint-500" data-id="element-28">*</span>
                        </label>
                        <input type="text" id="contact" value={contact} onChange={e => setContact(e.target.value)} required className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-mint-500/50 focus:border-mint-500 transition-colors" placeholder="email@domain.com or (555) 555-5555" data-id="element-29" />
                      </div>

                      {/* Social Handles */}
                      <div data-id="element-30">
                        <label htmlFor="social" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-31">
                          Social handles / links{' '}
                          <span className="text-mint-500" data-id="element-32">*</span>
                        </label>
                        <input type="text" id="social" value={socialHandles} onChange={e => setSocialHandles(e.target.value)} required className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-mint-500/50 focus:border-mint-500 transition-colors" placeholder="@handle or profile URLs" data-id="element-33" />
                      </div>

                      {/* Why - Optional, written only */}
                      <div data-id="element-34">
                        <label htmlFor="why" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-35">
                          Why you're a fit{' '}
                          <span className="text-slate-500 font-normal" data-id="element-36">
                            (optional)
                          </span>
                        </label>
                        <textarea id="why" value={whyText} onChange={e => setWhyText(e.target.value)} rows={4} className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-mint-500/50 focus:border-mint-500 transition-colors resize-none" placeholder="Tell us about your audience and why you'd be a great fit for the Creator Lab..." data-id="element-37" />
                      </div>

                      {/* Error Message */}
                      {submitError && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-id="element-38">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-39" />
                          <span data-id="element-40">{submitError}</span>
                        </motion.div>}

                      {/* Submit */}
                      <Button type="submit" variant="primary" disabled={!isValid || isSubmitting} className="w-full bg-gradient-to-r from-mint-500 to-wave-500 border-none text-navy-950 font-bold py-4 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-41">
                        {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-42">
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-43" />
                            Submitting...
                          </span> : 'Submit Application'}
                      </Button>
                    </form>
                  </>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}